<script setup>
import { computed } from "vue";

const props = defineProps({
  label: { type: String, default: "" },
  abbreviation: { type: String, default: "" },
  accentColor: { type: String, default: "#5baee9" },
  fallback: { type: String, default: "--" },
  size: { type: [String, Number], default: 34 },
  mode: { type: String, default: "text" },
  hasAlert: { type: Boolean, default: false },
  alertTooltip: { type: String, default: "" },
  alertLabel: { type: String, default: "Alertas" },
});

const displayText = computed(() => {
  const abbreviation = String(props.abbreviation || "").trim();
  if (abbreviation) {
    return abbreviation.toUpperCase().slice(0, 3);
  }

  const base = String(props.label || props.fallback || "--")
    .trim();
  if (!base) return "--";

  const words = base
    .split(" ")
    .map((word) => word.trim())
    .filter(Boolean);

  if (!words.length) return "--";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0] || ""}${words[1][0] || ""}`.toUpperCase();
});

const avatarStyle = computed(() => {
  const normalizedSize = Number.parseInt(props.size, 10);
  const size = Number.isFinite(normalizedSize) && normalizedSize > 0 ? normalizedSize : 34;
  const accent = String(props.accentColor || "#5baee9").trim() || "#5baee9";

  return {
    "--vehicle-avatar-accent": accent,
    "--vehicle-avatar-soft": `color-mix(in srgb, ${accent} 14%, white)`,
    "--vehicle-avatar-sheen": `color-mix(in srgb, white 88%, ${accent})`,
    "--vehicle-avatar-border": `color-mix(in srgb, ${accent} 22%, white)`,
    "--vehicle-avatar-shadow": `color-mix(in srgb, ${accent} 20%, transparent)`,
    "--vehicle-avatar-dot": `color-mix(in srgb, ${accent} 72%, white)`,
    height: `${size}px`,
    width: `${size}px`,
  };
});
</script>

<template>
  <span class="vehicle-avatar" :class="{ 'vehicle-avatar--dot': mode === 'dot' }" :style="avatarStyle">
    <span v-if="mode === 'dot'" class="vehicle-avatar-dot" aria-hidden="true"></span>
    <template v-else>
      {{ displayText }}
    </template>
    <span
      v-if="hasAlert"
      v-tooltip="alertTooltip"
      class="vehicle-avatar-alert"
      :aria-label="alertLabel"
    >
      <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
    </span>
  </span>
</template>

<style scoped lang="scss">
.vehicle-avatar {
  align-items: center;
  background:
    radial-gradient(circle at 30% 28%, var(--vehicle-avatar-sheen) 0%, transparent 48%),
    linear-gradient(180deg, var(--vehicle-avatar-sheen) 0%, var(--vehicle-avatar-soft) 100%);
  border: 1px solid var(--vehicle-avatar-border);
  border-radius: 999px;
  color: var(--vehicle-avatar-accent);
  display: inline-flex;
  font-size: 0.66rem;
  font-weight: 700;
  justify-content: center;
  letter-spacing: 0.04em;
  line-height: 1;
  position: relative;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.75),
    0 6px 14px -10px var(--vehicle-avatar-shadow);
  overflow: visible;
  text-transform: uppercase;
}

.vehicle-avatar--dot {
  background: transparent;
  border-color: transparent;
  box-shadow: none;
}

.vehicle-avatar-dot {
  background: var(--vehicle-avatar-dot);
  border-radius: 999px;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.45),
    0 0 0 1px color-mix(in srgb, var(--vehicle-avatar-accent) 18%, white);
  display: block;
  height: 50%;
  width: 50%;
}

.vehicle-avatar-alert {
  align-items: center;
  background: rgba(255, 245, 245, 0.98);
  border: 1.5px solid #ffffff;
  border-radius: 999px;
  box-shadow:
    0 0 0 1px rgba(244, 191, 191, 0.92),
    0 2px 5px rgba(162, 58, 58, 0.2);
  color: #a33a3a;
  display: inline-flex;
  height: 14px;
  justify-content: center;
  min-width: 14px;
  position: absolute;
  right: -3px;
  bottom: -5px;
}

.vehicle-avatar-alert i {
  color: inherit;
  font-size: 0.5rem;
  line-height: 1;
}
</style>
